import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  CssBaseline,
  GlobalStyles,
  StyledEngineProvider,
  ThemeProvider as MuiThemeProvider,
  useMediaQuery,
  type Theme,
} from '@mui/material'

import { lightTheme as defaultLightTheme, darkTheme as defaultDarkTheme } from './theme'
import { scrollbar } from './scrollbar'

/**
 * Single shared theme provider for all Vera frontends (ADR 0002).
 *
 * Replaces the per-app, partly-inline ThemeProvider setups (platform App.tsx
 * inline themes + a per-component useColorMode useState; app's settings/theme).
 * Centralizes: color-mode state (shared via context, not per-component),
 * persistence (localStorage), system-preference default + live updates, and the
 * MUI ThemeProvider + CssBaseline + StyledEngineProvider wiring.
 *
 * Apps may pass their own `lightTheme`/`darkTheme` during the per-epic migration
 * to the shared `@repo/ui` theme; both default to the canonical shared themes.
 */

export type ColorMode = 'light' | 'dark' | 'system'
export type ResolvedColorMode = 'light' | 'dark'

interface ColorModeContextValue {
  /** User selection — may be 'system'. */
  mode: ColorMode
  /** Concrete mode actually applied (system resolved to light/dark). */
  resolvedMode: ResolvedColorMode
  setMode: (mode: ColorMode) => void
  /** Toggle between light and dark (sets an explicit, non-system mode). */
  toggle: () => void
}

const ColorModeContext = createContext<ColorModeContextValue | null>(null)

export interface VeraThemeProviderProps {
  children: ReactNode
  /** Initial mode when nothing is persisted. Default 'system'. */
  defaultMode?: ColorMode
  /** Override the light theme (per-app migration). Defaults to shared. */
  lightTheme?: Theme
  /** Override the dark theme (per-app migration). Defaults to shared. */
  darkTheme?: Theme
  /** localStorage key for persisting the user's choice. */
  storageKey?: string
  /**
   * The mode for THIS navigation, when the caller already knows it from an
   * explicit act the visitor just performed somewhere this provider cannot
   * see. Wins over both the persisted value and `defaultMode`.
   *
   * The live caller is the platform's website hand-off: vera.inc puts the
   * visitor's colour-mode choice on the login link as `?theme=`, because
   * localStorage is per-ORIGIN and a choice made on vera.inc does not reach
   * platform.vera.inc. See `utils/colorModeHandoff` in apps/platform.
   *
   * DELIBERATELY NOT PERSISTED. It seeds this mount only; `setMode` remains
   * the sole writer of the stored value. Persisting it would let one
   * navigation's hand-off outlive itself and answer for every later arrival,
   * which is exactly the cross-context leak `ignoreStoredMode` exists to stop.
   */
  initialMode?: ColorMode
  /**
   * Do not read the persisted mode at mount; fall back to `defaultMode`
   * ('system') instead.
   *
   * The stored value is a preference for the AUTHENTICATED app — on the
   * platform it is written by `setMode`, and the only callers of `setMode`
   * are the signed-in theme control and the profile hydrator. So on a
   * PRE-AUTH page it is somebody's leftover session state, not a statement
   * about the person now looking at the screen: a visitor arriving cold on a
   * signup link, in a browser where anyone once signed in, would otherwise
   * be served that stranger's theme rather than their own system setting.
   *
   * Reads only. Writes are still honoured, since a `setMode` call is an
   * explicit act by whoever is at the keyboard right now.
   */
  ignoreStoredMode?: boolean
}

function readStoredMode(key: string, fallback: ColorMode): ColorMode {
  if (typeof window === 'undefined') return fallback
  const v = window.localStorage.getItem(key)
  return v === 'light' || v === 'dark' || v === 'system' ? v : fallback
}

export function VeraThemeProvider({
  children,
  defaultMode = 'system',
  lightTheme = defaultLightTheme,
  darkTheme = defaultDarkTheme,
  storageKey = 'vera-color-mode',
  initialMode,
  ignoreStoredMode = false,
}: VeraThemeProviderProps) {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)')
  // Three sources, most-explicit first. Resolved ONCE, in the initialiser, so the
  // first paint is already correct and nothing has to wash from one theme to another
  // (the website provider's own docblock records what that costs when it goes wrong).
  //
  //   1. `initialMode`      — an explicit act on THIS navigation (the `?theme=`
  //                           hand-off). Most recent, so it wins.
  //   2. the persisted mode — the authenticated app's preference. Skipped entirely
  //                           when `ignoreStoredMode`, i.e. on a pre-auth page.
  //   3. `defaultMode`      — 'system', the honest answer when nobody at this
  //                           keyboard has told us anything.
  //
  // The signed-in profile's saved theme is NOT a fourth source here. It arrives
  // later and asynchronously, through `setMode` (platform's `ThemeHydrator`), which
  // is correct: it can only be known after a session exists, and by then rule 2 is
  // the one that will carry it across the next reload.
  const [mode, setModeState] = useState<ColorMode>(
    () =>
      initialMode ??
      (ignoreStoredMode ? defaultMode : readStoredMode(storageKey, defaultMode)),
  )

  const setMode = (next: ColorMode) => {
    setModeState(next)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(storageKey, next)
    }
  }

  const resolvedMode: ResolvedColorMode =
    mode === 'system' ? (prefersDark ? 'dark' : 'light') : mode

  const toggle = () => setMode(resolvedMode === 'dark' ? 'light' : 'dark')

  const activeTheme = resolvedMode === 'dark' ? darkTheme : lightTheme

  // Keep CSS color-scheme in sync (native form controls, scrollbars), and the <html>
  // background with it. The platform's index.html paints that background BEFORE the bundle
  // runs (so a dark visitor does not see a white page while it downloads); once set there it
  // stops the body's background propagating to the canvas, so it must follow a later toggle
  // too or overscroll / short pages would show the stale colour.
  const pageBackground = activeTheme.palette.background.default
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.style.colorScheme = resolvedMode
      document.documentElement.style.backgroundColor = pageBackground
    }
  }, [resolvedMode, pageBackground])

  const ctx = useMemo<ColorModeContextValue>(
    () => ({ mode, resolvedMode, setMode, toggle }),
    [mode, resolvedMode],
  )

  return (
    <ColorModeContext.Provider value={ctx}>
      <StyledEngineProvider injectFirst>
        <MuiThemeProvider theme={activeTheme}>
          <CssBaseline />
          {/* House scrollbar for every scroll container in the app. After CssBaseline so
              it wins over the reset; see scrollbar.ts for why it is not folded into the
              baseline's style string. */}
          <GlobalStyles styles={scrollbar(activeTheme)} />
          {children}
        </MuiThemeProvider>
      </StyledEngineProvider>
    </ColorModeContext.Provider>
  )
}

/**
 * Read/control the active color mode. Must be called within VeraThemeProvider.
 * Includes legacy convenience setters for back-compat with platform's prior hook.
 */
export function useColorMode() {
  const ctx = useContext(ColorModeContext)
  if (!ctx) {
    throw new Error('useColorMode must be used within a VeraThemeProvider')
  }
  return {
    ...ctx,
    setLightMode: () => ctx.setMode('light'),
    setDarkMode: () => ctx.setMode('dark'),
    setSystemMode: () => ctx.setMode('system'),
    // legacy typo alias (platform's prior hook)
    setSystemtMode: () => ctx.setMode('system'),
  }
}
